import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type { Doctor } from '../../types/doctor';

interface DoctorDeleteModalProps {
  open: boolean;
  doctor: Doctor | null;
  onClose: () => void;
  onConfirm: (id: number) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * 医師削除確認モーダルコンポーネント
 * 
 * 意図：
 * - 削除操作の二重確認
 * - 削除対象の明確な表示
 * - 誤操作の防止
 * 
 * 注意点：
 * - 削除は取り消し不可能
 * - エラーハンドリング実装済み
 * 
 * 改善提案：
 * - ソフトデリート（論理削除）の実装
 * - 削除前の関連データチェック
 * - 削除履歴の記録
 */
export const DoctorDeleteModal: React.FC<DoctorDeleteModalProps> = ({
  open,
  doctor,
  onClose,
  onConfirm,
  isLoading = false,
  error
}) => {
  const theme = useTheme();

  if (!doctor) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm(doctor.id);
    } catch (error) {
      // エラーハンドリングは親コンポーネントで実施
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.error.main, 0.1)
            }}
          >
            <WarningIcon color="error" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              医師情報の削除確認
            </Typography>
            <Typography variant="caption" color="text.secondary">
              この操作は取り消せません
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          以下の医師情報を削除してもよろしいですか？
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.grey[100], 0.5),
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                氏名:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {doctor.first_name}{doctor.last_name}先生
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                医師コード:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {doctor.doctor_code}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                ステータス:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {doctor.status}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            削除すると、この医師に関連する以下の情報も失われます：
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">シフト情報</Typography>
            </li>
            <li>
              <Typography variant="body2">勤務履歴</Typography>
            </li>
            <li>
              <Typography variant="body2">その他の関連データ</Typography>
            </li>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          color="inherit"
        >
          キャンセル
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {isLoading ? '削除中...' : '削除する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
